import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import { fetchIssues } from "./issues/issueService";
import { fetchAllTasks, fetchProjectUsers } from "./taskService";
import { fetchSubTasksByTaskId } from "./subTaskService";
import { fetchUserStories } from "./userStoryService";
import { getCurrentUserRole, getAllowedSidebarPages } from "../utils/roleAccess";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import "../assets/css/DigixLeads.css";
import "../assets/css/Projects.css";
import "../assets/css/Dashboard.css";

const CARD_KEY_TO_PAGE = {
  projects: "Projects",
  digixLeads: "DigixLeads",
  customers: "Clients",
  accounts: "Accounts",
  issues: "Issues",
};

function toCount(arr) {
  if (Array.isArray(arr)) return arr.length;
  if (arr?.data && Array.isArray(arr.data)) return arr.data.length;
  if (arr?.projects && Array.isArray(arr.projects)) return arr.projects.length;
  if (arr?.results && Array.isArray(arr.results)) return arr.results.length;
  return 0;
}

function toRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.leads)) return payload.leads;
  return [];
}

function unwrapLeadRow(row) {
  if (!row || typeof row !== "object") return row;
  return (
    row.training_lead ||
    row.internship_lead ||
    row.job_lead ||
    row.real_estate_lead ||
    row.business_lead ||
    row.lead ||
    row
  );
}

function getLeadStatusText(row) {
  const lead = unwrapLeadRow(row);
  const direct =
    lead?.Status ??
    lead?.status ??
    lead?.Status_Name ??
    lead?.status_name ??
    lead?.Lead_Status ??
    lead?.lead_status;
  if (direct != null && String(direct).trim() !== "") return String(direct);
  const nested =
    lead?.statusObj?.Status ||
    lead?.statusObj?.status ||
    lead?.status_obj?.Status ||
    lead?.status_obj?.status ||
    lead?.status_details?.Status ||
    lead?.status_details?.status;
  return nested != null ? String(nested) : "";
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function isActiveProject(project) {
  const status = normalizeText(project?.Status ?? project?.status);
  return status === "active";
}

function isResolvedProject(project) {
  const status = normalizeText(project?.Status ?? project?.status);
  if (!status) return false;
  return (
    status.includes("resolved") ||
    status.includes("complete") ||
    status.includes("completed") ||
    status.includes("closed") ||
    status.includes("done")
  );
}

function isNewProject(project) {
  const status = normalizeText(
    project?.Status ?? project?.status ?? project?.Status_Name ?? project?.status_name
  );
  return status.includes("new");
}

function isInProgressProject(project) {
  const status = normalizeText(
    project?.Status ?? project?.status ?? project?.Status_Name ?? project?.status_name
  );
  const compact = status.replace(/[\s_-]+/g, "");
  return (
    status.includes("in progress") ||
    status.includes("in-progress") ||
    status.includes("in_progress") ||
    compact.includes("inprogress")
  );
}

function isOpenIssue(issue) {
  const status = normalizeText(issue?.Status ?? issue?.status);
  if (!status) return false;
  const compact = status.replace(/[\s_-]+/g, "");
  // Align with Issues page "Open" filter behavior.
  // Avoid counting "reopened" as "open".
  if (compact.includes("reopen")) return false;
  return status === "open" || status.includes("open");
}

function isResolvedIssue(issue) {
  const status = normalizeText(issue?.Status ?? issue?.status);
  if (!status) return false;
  return (
    status.includes("resolved") ||
    status.includes("closed") ||
    status.includes("complete") ||
    status.includes("done")
  );
}

function isInProgressIssue(issue) {
  const status = normalizeText(issue?.Status ?? issue?.status);
  if (!status) return false;
  // Reopened can include "open" substring, but we treat it separately.
  const compact = status.replace(/[\s_-]+/g, "");
  if (compact.includes("reopen")) return false;
  // Match both "In Progress" and "inprogress" (and similar spacing/hyphen variants).
  return compact === "inprogress" || status.includes("in progress") || status.includes("inprogress");
}

function isReopenedIssue(issue) {
  const status = normalizeText(issue?.Status ?? issue?.status);
  if (!status) return false;
  // Handle "Reopened", "Re-opened", etc.
  const compact = status.replace(/[\s_-]+/g, "");
  return compact.includes("reopen");
}

function dedupeIssuesById(list) {
  const arr = Array.isArray(list) ? list : [];
  const map = new Map();
  const noIdRows = [];
  for (const issue of arr) {
    const id = issue?.Issue_Id ?? issue?.issue_id ?? issue?.id;
    if (id == null) {
      noIdRows.push(issue);
      continue;
    }
    if (!map.has(String(id))) map.set(String(id), issue);
  }
  return [...map.values(), ...noIdRows];
}

function parsePossibleIds(value) {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((v) => Number(String(v).trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
  }
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? [n] : [];
}

function getIssueAssignedUserIds(issue) {
  const ids = [];
  const assignedUsers = Array.isArray(issue?.Assigned_Users)
    ? issue.Assigned_Users
    : Array.isArray(issue?.assigned_users)
      ? issue.assigned_users
      : [];
  for (const u of assignedUsers) {
    const uid = Number(u?.User_Id ?? u?.user_id ?? u?.id);
    if (Number.isFinite(uid) && uid > 0) ids.push(uid);
  }
  ids.push(
    ...parsePossibleIds(issue?.Assigned_User_Ids),
    ...parsePossibleIds(issue?.assigned_user_ids),
    ...parsePossibleIds(issue?.Assigned_User_Id),
    ...parsePossibleIds(issue?.assigned_user_id),
    ...parsePossibleIds(issue?.Assigned_To),
    ...parsePossibleIds(issue?.assigned_to),
    ...parsePossibleIds(issue?.Assigned_To_Id),
    ...parsePossibleIds(issue?.assigned_to_id)
  );
  return Array.from(new Set(ids));
}

function isIssueAssignedToUser(issue, userId) {
  if (userId == null) return false;
  const ids = getIssueAssignedUserIds(issue);
  return ids.includes(Number(userId));
}

function getTaskAssignedUserIds(task) {
  const ids = [];
  const assignedUsers = Array.isArray(task?.Assigned_Users)
    ? task.Assigned_Users
    : Array.isArray(task?.assigned_users)
      ? task.assigned_users
      : [];
  for (const u of assignedUsers) {
    const uid = Number(u?.User_Id ?? u?.user_id ?? u?.id);
    if (Number.isFinite(uid) && uid > 0) ids.push(uid);
  }
  ids.push(
    ...parsePossibleIds(task?.Assigned_User_Ids),
    ...parsePossibleIds(task?.assigned_user_ids),
    ...parsePossibleIds(task?.Assigned_User_Id),
    ...parsePossibleIds(task?.assigned_user_id),
    ...parsePossibleIds(task?.Assigned_To),
    ...parsePossibleIds(task?.assigned_to),
    ...parsePossibleIds(task?.Assigned_To_Id),
    ...parsePossibleIds(task?.assigned_to_id)
  );
  return Array.from(new Set(ids));
}

function isTaskAssignedToUser(task, userId) {
  const assignedUsers = Array.isArray(task?.Assigned_Users)
    ? task.Assigned_Users
    : Array.isArray(task?.assigned_users)
      ? task.assigned_users
      : [];

  // Highest priority: explicit Assigned Users list (matches View Task screen).
  if (assignedUsers.length > 0) {
    if (userId == null) return false;
    const idsFromAssignedUsers = assignedUsers
      .map((u) => Number(u?.User_Id ?? u?.user_id ?? u?.id))
      .filter((n) => Number.isFinite(n) && n > 0);
    return idsFromAssignedUsers.includes(Number(userId));
  }

  // Fallback for rows that do not include Assigned_Users.
  const ids = getTaskAssignedUserIds(task);
  if (userId != null && ids.includes(Number(userId))) return true;
  return false;
}

function getProjectAssignedUserIds(project) {
  const ids = [];
  const users = Array.isArray(project?.Users)
    ? project.Users
    : Array.isArray(project?.users)
      ? project.users
      : [];
  for (const u of users) {
    const uid = Number(u?.User_Id ?? u?.user_id ?? u?.id);
    if (Number.isFinite(uid) && uid > 0) ids.push(uid);
  }
  ids.push(
    ...parsePossibleIds(project?.User_Ids),
    ...parsePossibleIds(project?.user_ids),
    ...parsePossibleIds(project?.Assigned_User_Ids),
    ...parsePossibleIds(project?.assigned_user_ids),
    ...parsePossibleIds(project?.Users_Ids),
    ...parsePossibleIds(project?.users_ids)
  );
  return Array.from(new Set(ids));
}

function isProjectAssignedToUserFromRow(project, userId) {
  if (userId == null) return false;
  return getProjectAssignedUserIds(project).includes(Number(userId));
}

function taskBucket(statusRaw) {
  const status = normalizeText(statusRaw);
  const compact = status.replace(/[\s_-]+/g, "");
  if (
    status.includes("to-do") ||
    status.includes("to do") ||
    status.includes("todo") ||
    compact === "todo"
  ) {
    return "todo";
  }
  if (
    status.includes("in progress") ||
    status.includes("in-progress") ||
    status.includes("in_progress") ||
    compact === "inprogress"
  ) {
    return "inProgress";
  }
  return "other";
}

async function fetchAllSubTasksFromTasks(taskRows) {
  const taskContextById = new Map();
  const taskIds = (Array.isArray(taskRows) ? taskRows : [])
    .map((t) => {
      const rawId = t?.Task_Id ?? t?.task_id ?? t?.id;
      if (rawId == null || rawId === "") return null;
      const taskId = Number(rawId);
      if (!Number.isFinite(taskId) || taskId <= 0) return null;
      taskContextById.set(String(taskId), {
        taskId,
        projectId: t?.Project_Id ?? t?.project_id ?? t?.ProjectId ?? t?.projectId ?? null,
        subProjectId: t?.Sub_Project_Id ?? t?.sub_project_id ?? t?.SubProjectId ?? t?.subProjectId ?? null,
        storyId: t?.Story_Id ?? t?.story_id ?? t?.StoryId ?? t?.storyId ?? null,
      });
      return taskId;
    })
    .filter((id) => id != null);

  if (taskIds.length === 0) return [];

  const lists = await Promise.all(taskIds.map((taskId) => fetchSubTasksByTaskId(taskId).catch(() => [])));
  const flat = lists.flat();
  const seen = new Set();
  return flat.filter((st, idx) => {
    const id = st?.Sub_Task_Id ?? st?.sub_task_id ?? st?.id ?? `idx-${idx}`;
    const key = String(id);
    if (seen.has(key)) return false;
    seen.add(key);
    const taskId = Number(st?.Task_Id ?? st?.task_id ?? st?.TaskId ?? st?.taskId);
    const ctx = Number.isFinite(taskId) ? taskContextById.get(String(taskId)) : null;
    if (ctx) {
      st.__taskId = ctx.taskId;
      st.__projectId = ctx.projectId;
      st.__subProjectId = ctx.subProjectId;
      st.__storyId = ctx.storyId;
    }
    return true;
  });
}

function leadBucket(statusRaw) {
  const status = normalizeText(statusRaw);
  if (!status) return "other";
  if (status.includes("new")) return "newLeads";
  if (status.includes("waiting") && status.includes("confirm")) return "waiting";
  if (status.includes("next") && status.includes("follow")) return "nextFollowup";
  if (status.includes("follow")) return "followup";
  return "other";
}

const DIGIX_MODULE_DEFS = [
  { key: "business", title: "Business Leads", path: "/business-leads" },
  { key: "realestate", title: "Real Estate Leads", path: "/realestate-leads" },
  { key: "training", title: "Training Leads", path: "/training-leads" },
  { key: "job", title: "Job Leads", path: "/job-leads" },
  { key: "internship", title: "Internship Leads", path: "/internship-leads" },
];

function emptyDigixModules() {
  return DIGIX_MODULE_DEFS.map((d) => ({
    key: d.key,
    title: d.title,
    path: d.path,
    total: 0,
    newLeads: 0,
    waiting: 0,
    nextFollowup: 0,
    followup: 0,
  }));
}

function digixCountsForLeadRows(rows) {
  const out = { total: rows.length, newLeads: 0, waiting: 0, nextFollowup: 0, followup: 0 };
  for (const lead of rows) {
    const b = leadBucket(getLeadStatusText(lead));
    if (b === "newLeads") out.newLeads += 1;
    if (b === "waiting") out.waiting += 1;
    if (b === "nextFollowup") out.nextFollowup += 1;
    if (b === "followup") out.followup += 1;
  }
  return out;
}

/** Status labels passed to DigixLeads via navigation state (must match /lead-statuses and lead row status text, compared case-insensitively). */
const DIGIX_BUCKET_TO_STATUS = {
  newLeads: "New",
  waiting: "Waiting for Confirmation",
  followup: "Followup",
  nextFollowup: "Next Followup",
  total: "All Statuses",
};

function digixNavigateState(bucket) {
  const status = DIGIX_BUCKET_TO_STATUS[bucket] ?? "All Statuses";
  return { digixFilter: { status } };
}

function DashboardDigixTable({ modules, navigate }) {
  return (
    <div className="dashboard-digix-table-card">
      <div className="dashboard-digix-table-card__header">
        <h3 className="dashboard-digix-table-card__title">DigiXLeads</h3>
      </div>

      <div className="dashboard-digix-table-wrap">
        <table className="dashboard-digix-table">
            <thead>
              <tr>
                <th className="dashboard-digix-table__th dashboard-digix-table__th--module">Module</th>
                <th className="dashboard-digix-table__th">New leads</th>
                <th className="dashboard-digix-table__th">Waiting for confirmation</th>
                <th className="dashboard-digix-table__th">Followup</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((mod) => (
                <tr key={mod.key} className="dashboard-digix-table__row">
                  <td className="dashboard-digix-table__td dashboard-digix-table__td--module">
                    <button
                      type="button"
                      className="dashboard-digix-cell-btn dashboard-digix-cell-btn--module"
                      onClick={() => navigate(mod.path, { state: digixNavigateState("total") })}
                    >
                      {mod.title}
                    </button>
                  </td>
                  <td className="dashboard-digix-table__td dashboard-digix-table__td--num">
                    <button
                      type="button"
                      className="dashboard-digix-cell-btn"
                      onClick={() => navigate(mod.path, { state: digixNavigateState("newLeads") })}
                    >
                      {mod.newLeads}
                    </button>
                  </td>
                  <td className="dashboard-digix-table__td dashboard-digix-table__td--num">
                    <button
                      type="button"
                      className="dashboard-digix-cell-btn"
                      onClick={() => navigate(mod.path, { state: digixNavigateState("waiting") })}
                    >
                      {mod.waiting}
                    </button>
                  </td>
                  <td className="dashboard-digix-table__td dashboard-digix-table__td--num">
                    <button
                      type="button"
                      className="dashboard-digix-cell-btn"
                      onClick={() => navigate(mod.path, { state: digixNavigateState("followup") })}
                    >
                      {mod.followup}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
    </div>
  );
}

function DashboardCollapsibleTableCard({ title, cardClassName, children, collapsible = true }) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className={cardClassName}>
      <div className="dashboard-digix-table-card__header">
        <h3 className="dashboard-digix-table-card__title">{title}</h3>
        {collapsible ? (
          <button
            type="button"
            className="dashboard-digix-table-card__collapse-btn"
            aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((v) => !v)}
          >
            <span className="dashboard-digix-table-card__collapse-icon">
              {collapsed ? <FaChevronDown /> : <FaChevronUp />}
            </span>
          </button>
        ) : null}
      </div>
      {!collapsible || !collapsed ? children : null}
    </div>
  );
}

function DashboardTasksOverviewTable({ rows, navigate }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return <div className="dashboard-customer-projects-empty">No task data found.</div>;
  }

  const openTask = (row) => {
    const preset = row?.statusBucket === "inProgress" ? "inProgress" : "todo";
    navigate("/projects", {
      state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: preset },
    });
  };

  const openSubTask = (row) => {
    const pid = row?.projectId;
    const sid = row?.storyId;
    const tid = row?.taskId;
    const stid = row?.subTaskId;
    if (pid && sid && tid && stid) {
      if (row?.subProjectId) {
        navigate(`/projects/${pid}/subprojects/${row.subProjectId}/user-stories/${sid}/tasks/${tid}/subtasks/${stid}/view`);
      } else {
        navigate(`/projects/${pid}/user-stories/${sid}/tasks/${tid}/subtasks/${stid}/view`);
      }
      return;
    }
    if (pid && sid && tid) {
      if (row?.subProjectId) {
        navigate(`/projects/${pid}/subprojects/${row.subProjectId}/user-stories/${sid}/tasks/${tid}/view`);
      } else {
        navigate(`/projects/${pid}/user-stories/${sid}/tasks/${tid}/view`);
      }
      return;
    }
    openTask(row);
  };

  const activateRow = (row) => {
    if (row.type === "Subtask") openSubTask(row);
    else openTask(row);
  };

  return (
    <div className="dashboard-customer-projects-wrap">
      <table className="dashboard-digix-table dashboard-customer-projects-table">
        <thead>
          <tr>
            <th className="dashboard-digix-table__th dashboard-digix-table__th--module">Title</th>
            <th className="dashboard-digix-table__th">Type</th>
            <th className="dashboard-digix-table__th">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              className="dashboard-digix-table__row dashboard-digix-table__row--clickable"
              tabIndex={0}
              onClick={() => activateRow(row)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  activateRow(row);
                }
              }}
              aria-label={
                row.type === "Subtask"
                  ? `Open subtask ${row.title}`
                  : `Open task ${row.title}`
              }
            >
              <td className="dashboard-digix-table__td dashboard-digix-table__td--module">{row.title}</td>
              <td className="dashboard-digix-table__td dashboard-digix-table__td--num">{row.type}</td>
              <td className="dashboard-digix-table__td dashboard-digix-table__td--num">{row.statusLabel}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DashboardIssuesOverviewTable({ counts, navigate }) {
  const openCount = Number(counts?.open ?? 0);
  const inProgressCount = Number(counts?.inProgress ?? 0);
  const reopenedCount = Number(counts?.reopened ?? 0);

  const goToIssues = (preset) => {
    navigate("/issues", { state: { dashboardIssueStatusPreset: preset } });
  };

  return (
    <div className="dashboard-customer-projects-wrap">
      <table className="dashboard-digix-table dashboard-customer-projects-table">
        <thead>
          <tr>
            <th className="dashboard-digix-table__th dashboard-digix-table__th--module">Type</th>
            <th className="dashboard-digix-table__th">Open</th>
            <th className="dashboard-digix-table__th">In Progress</th>
            <th className="dashboard-digix-table__th">Reopened</th>
          </tr>
        </thead>
        <tbody>
          <tr className="dashboard-digix-table__row">
            <td className="dashboard-digix-table__td dashboard-digix-table__td--module">Issues</td>
            <td className="dashboard-digix-table__td dashboard-digix-table__td--num">
              <button type="button" className="dashboard-digix-cell-btn" onClick={() => goToIssues("open")}>
                {openCount}
              </button>
            </td>
            <td className="dashboard-digix-table__td dashboard-digix-table__td--num">
              <button type="button" className="dashboard-digix-cell-btn" onClick={() => goToIssues("inProgress")}>
                {inProgressCount}
              </button>
            </td>
            <td className="dashboard-digix-table__td dashboard-digix-table__td--num">
              <button type="button" className="dashboard-digix-cell-btn" onClick={() => goToIssues("reopened")}>
                {reopenedCount}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function buildDigixModulesFromLeadResponses(leadsRes, realestateRes, trainingRes, jobRes, internshipRes) {
  const leadRowSets = [
    toRows(leadsRes?.data ?? leadsRes),
    toRows(realestateRes?.data ?? realestateRes),
    toRows(trainingRes?.data ?? trainingRes),
    toRows(jobRes?.data ?? jobRes),
    toRows(internshipRes?.data ?? internshipRes),
  ];
  return DIGIX_MODULE_DEFS.map((def, i) => ({
    key: def.key,
    title: def.title,
    path: def.path,
    ...digixCountsForLeadRows(leadRowSets[i]),
  }));
}

async function fetchDigixModulesFromApis() {
  const [leadsRes, realestateRes, trainingRes, internshipRes, jobRes] = await Promise.all([
    axiosInstance.get("/leads").catch(() => ({ data: [] })),
    axiosInstance.get("/real-estate-leads").catch(() => ({ data: [] })),
    axiosInstance.get("/training-leads").catch(() => ({ data: [] })),
    axiosInstance.get("/internship-leads").catch(() => ({ data: [] })),
    axiosInstance.get("/job-leads").catch(() => ({ data: [] })),
  ]);
  return buildDigixModulesFromLeadResponses(leadsRes, realestateRes, trainingRes, jobRes, internshipRes);
}

async function fetchSubprojectIdsForDashboard(pid) {
  try {
    let response = await axiosInstance.get(`/sub-projects/project/${pid}`);
    let raw = response.data;
    let arr = [];
    if (Array.isArray(raw)) arr = raw;
    else if (raw?.sub_projects && Array.isArray(raw.sub_projects)) arr = raw.sub_projects;
    else if (raw?.data && Array.isArray(raw.data)) arr = raw.data;
    else {
      const r2 = await axiosInstance.get(`/projects/${pid}/sub-projects`).catch(() => ({ data: [] }));
      raw = r2.data;
      arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
    }
    return (arr || [])
      .map((sp) => Number(sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id))
      .filter((n) => Number.isFinite(n) && n > 0);
  } catch {
    return [];
  }
}

async function fetchAllUserStoriesAggregated(projectRows) {
  const pids = (projectRows || [])
    .map((p) => p.Project_Id ?? p.project_id ?? p.id)
    .filter((id) => id != null && Number(id) > 0)
    .map(Number);
  const perProject = await Promise.all(
    pids.map(async (pid) => {
      const chunk = [];
      const pl = await fetchUserStories(pid, undefined).catch(() => []);
      chunk.push(...(pl || []));
      const sids = await fetchSubprojectIdsForDashboard(pid);
      await Promise.all(
        sids.map(async (sid) => {
          const st = await fetchUserStories(pid, sid).catch(() => []);
          chunk.push(...(st || []));
        })
      );
      return chunk;
    })
  );
  const all = perProject.flat();
  const seen = new Set();
  const deduped = [];
  for (const s of all) {
    const id = s?.User_Story_Id ?? s?.user_story_id ?? s?.Story_Id ?? s?.story_id;
    if (id != null) {
      const k = String(id);
      if (seen.has(k)) continue;
      seen.add(k);
    }
    deduped.push(s);
  }
  return deduped;
}

function isUserStoryCreatedByUser(story, userId, userNameNormalized) {
  const createdId = Number(
    story?.Created_By ?? story?.created_by ?? story?.Created_By_Id ?? story?.created_by_id
  );
  if (Number.isFinite(createdId) && userId != null && createdId === Number(userId)) return true;
  const nm = normalizeText(
    story?.Created_By_Name ?? story?.created_by_name ?? story?.Creator_Name ?? story?.creator_name
  );
  return Boolean(userNameNormalized && nm === userNameNormalized);
}

function isPaymentCompletedRow(p) {
  const s = normalizeText(p?.Payment_Status ?? p?.payment_status ?? "");
  if (!s) return false;
  return (
    s.includes("complete") ||
    s.includes("paid") ||
    s.includes("received") ||
    s.includes("cleared") ||
    s.includes("settled") ||
    s === "success"
  );
}

function financePaymentCompletedDueCounts(payments) {
  let completed = 0;
  let due = 0;
  for (const p of payments || []) {
    if (isPaymentCompletedRow(p)) completed += 1;
    else due += 1;
  }
  return { completed, due };
}

function buildTaskOverviewRows(taskRows, subtaskRows) {
  const rows = [];
  for (const t of taskRows || []) {
    const b = taskBucket(t?.Status ?? t?.status ?? t?.Status_Name ?? t?.status_name);
    if (b !== "todo" && b !== "inProgress") continue;
    rows.push({
      key: `task-${t?.Task_Id ?? t?.task_id ?? t?.id}`,
      type: "Task",
      title: String(t?.Title ?? t?.title ?? "Untitled task"),
      statusBucket: b,
      statusLabel: b === "todo" ? "To Do" : "In Progress",
      projectId: t?.Project_Id ?? t?.project_id ?? t?.ProjectId ?? t?.projectId ?? null,
      subProjectId: t?.Sub_Project_Id ?? t?.sub_project_id ?? t?.SubProjectId ?? t?.subProjectId ?? null,
      storyId: t?.Story_Id ?? t?.story_id ?? t?.StoryId ?? t?.storyId ?? null,
      taskId: t?.Task_Id ?? t?.task_id ?? t?.id ?? null,
      subTaskId: null,
    });
  }
  for (const st of subtaskRows || []) {
    const b = taskBucket(st?.Status ?? st?.status ?? st?.Status_Name ?? st?.status_name);
    if (b !== "todo" && b !== "inProgress") continue;
    rows.push({
      key: `subtask-${st?.Sub_Task_Id ?? st?.sub_task_id ?? st?.id}`,
      type: "Subtask",
      title: String(st?.Title ?? st?.title ?? "Untitled subtask"),
      statusBucket: b,
      statusLabel: b === "todo" ? "To Do" : "In Progress",
      projectId: st?.Project_Id ?? st?.project_id ?? st?.__projectId ?? null,
      subProjectId: st?.Sub_Project_Id ?? st?.sub_project_id ?? st?.__subProjectId ?? null,
      storyId: st?.Story_Id ?? st?.story_id ?? st?.__storyId ?? null,
      taskId: st?.Task_Id ?? st?.task_id ?? st?.__taskId ?? null,
      subTaskId: st?.Sub_Task_Id ?? st?.sub_task_id ?? st?.id ?? null,
    });
  }
  rows.sort((a, b) => {
    if (a.statusBucket !== b.statusBucket) return a.statusBucket === "todo" ? -1 : 1;
    if (a.type !== b.type) return a.type === "Task" ? -1 : 1;
    return a.title.localeCompare(b.title);
  });
  return rows;
}

async function fetchAllProjectPaymentsAggregated() {
  try {
    const res = await axiosInstance.get("/project-payments").catch(() => null);
    if (res?.data != null) {
      const raw = res.data;
      const arr = Array.isArray(raw) ? raw : raw?.payments ?? raw?.data ?? [];
      if (Array.isArray(arr) && arr.length > 0) return arr;
    }
  } catch {
    /* per-project fallback */
  }
  const projectsRes = await axiosInstance.get("/projects").catch(() => ({ data: [] }));
  const projects = Array.isArray(projectsRes?.data)
    ? projectsRes.data
    : Array.isArray(projectsRes?.data?.data)
      ? projectsRes.data.data
      : [];
  const merged = [];
  const seen = new Set();
  for (const proj of projects) {
    const pid = proj.Project_Id ?? proj.project_id ?? proj.id;
    if (pid == null) continue;
    let arr = [];
    const res1 = await axiosInstance.get("/project-payments", { params: { project_id: pid } }).catch(() => null);
    if (res1?.data) {
      const raw = res1.data;
      arr = Array.isArray(raw) ? raw : raw?.payments ?? raw?.data ?? [];
    }
    if (!arr.length) {
      const res2 = await axiosInstance.get(`/projects/${pid}/payments`).catch(() => null);
      if (res2?.data) {
        const raw = res2.data;
        arr = Array.isArray(raw) ? raw : raw?.payments ?? raw?.data ?? [];
      }
    }
    for (const p of arr || []) {
      const id = p.Payment_Id ?? p.payment_id ?? p.id;
      if (id != null) {
        const k = String(id);
        if (seen.has(k)) continue;
        seen.add(k);
      }
      merged.push(p);
    }
  }
  return merged;
}

function Dashboard() {
  const navigate = useNavigate();
  const role = getCurrentUserRole();
  const allowedPages = getAllowedSidebarPages(role);
  const isAdmin = useMemo(() => normalizeText(role) === "admin", [role]);
  const isDeveloper = useMemo(() => normalizeText(role).includes("developer"), [role]);
  const isTester = useMemo(() => normalizeText(role).includes("tester"), [role]);
  const isDevops = useMemo(() => normalizeText(role).includes("devops"), [role]);
  const isHr = useMemo(() => {
    const r = normalizeText(role);
    if (!r) return false;
    if (r.includes("human resource")) return true;
    const tokens = r.split(/[\s/_-]+/).filter(Boolean);
    if (tokens.includes("hr")) return true;
    if (tokens.some((t) => t.startsWith("hr-") || t.endsWith("-hr"))) return true;
    return false;
  }, [role]);
  /** HR-style dashboard: HR role name, or HR Management module in sidebar (and not dev/tester/devops/admin layout). */
  const showHrDashboard = useMemo(() => {
    if (isAdmin || isDeveloper || isTester || isDevops) return false;
    if (isHr) return true;
    return getAllowedSidebarPages(role).includes("HRManagement");
  }, [isAdmin, isDeveloper, isTester, isDevops, isHr, role]);
  const isBusinessAnalyst = useMemo(() => {
    const r = normalizeText(role);
    if (!r) return false;
    return r.includes("business analyst") || r.includes("businessanalyst");
  }, [role]);
  const showBaDashboard = useMemo(() => {
    if (isAdmin || isDeveloper || isTester || isDevops || showHrDashboard) return false;
    return isBusinessAnalyst;
  }, [isAdmin, isDeveloper, isTester, isDevops, showHrDashboard, isBusinessAnalyst]);
  const isFinanceTeam = useMemo(() => {
    const r = normalizeText(role);
    if (!r) return false;
    return (
      r.includes("finance") ||
      r.includes("accountant") ||
      r.includes("accounts receivable") ||
      r.includes("bookkeeper") ||
      r.includes("treasury")
    );
  }, [role]);
  const showFinanceDashboard = useMemo(() => {
    if (isAdmin || isDeveloper || isTester || isDevops || showHrDashboard || showBaDashboard) return false;
    return isFinanceTeam;
  }, [isAdmin, isDeveloper, isTester, isDevops, showHrDashboard, showBaDashboard, isFinanceTeam]);
  const isSalesTeam = useMemo(() => {
    const r = normalizeText(role);
    if (!r) return false;
    return (
      r.includes("sales") ||
      r.includes("relationship manager") ||
      r.includes("business development") ||
      r === "bdm" ||
      r.includes("account executive")
    );
  }, [role]);
  const showSalesDashboard = useMemo(() => {
    if (
      isAdmin ||
      isDeveloper ||
      isTester ||
      isDevops ||
      showHrDashboard ||
      showBaDashboard ||
      showFinanceDashboard
    ) {
      return false;
    }
    return isSalesTeam;
  }, [
    isAdmin,
    isDeveloper,
    isTester,
    isDevops,
    showHrDashboard,
    showBaDashboard,
    showFinanceDashboard,
    isSalesTeam,
  ]);
  const currentUserId = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      const id = Number(u.user_id ?? u.User_Id ?? u.id);
      return Number.isFinite(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  }, []);
  const currentUserName = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return String(u.user_name ?? u.User_Name ?? u.name ?? "").trim();
    } catch {
      return "";
    }
  }, []);
  const [counts, setCounts] = useState({
    projects: null,
    digixLeads: null,
    customers: null,
    accounts: null,
    issues: null,
  });
  const [adminStats, setAdminStats] = useState({
    projectsNew: 0,
    projectsInProgress: 0,
    projectsActive: 0,
    projectsCompleted: 0,
    tasksTodo: 0,
    tasksInProgress: 0,
    subtasksTodo: 0,
    subtasksInProgress: 0,
    issuesOpen: 0,
    issuesInProgress: 0,
    issuesReopened: 0,
    digixModules: emptyDigixModules(),
    customersTotal: 0,
  });
  const [salesDigixCardExpanded, setSalesDigixCardExpanded] = useState(false);
  const [permissionDigixCardExpanded, setPermissionDigixCardExpanded] = useState(false);
  const [roleTasksCardExpanded, setRoleTasksCardExpanded] = useState(false);
  const [roleTasksOverviewRows, setRoleTasksOverviewRows] = useState([]);
  const [developerStats, setDeveloperStats] = useState({
    assignedProjects: 0,
    assignedTasksTodo: 0,
    assignedTasksInProgress: 0,
    assignedIssuesOpen: 0,
    assignedIssuesInProgress: 0,
    assignedIssuesReopened: 0,
    assignedIssuesTotal: 0,
    assignedIssuesResolved: 0,
  });
  const [testerStats, setTesterStats] = useState({
    assignedProjects: 0,
    assignedTasksTodo: 0,
    assignedTasksInProgress: 0,
    myIssuesOpen: 0,
    myIssuesInProgress: 0,
    myIssuesReopened: 0,
    myIssuesTotal: 0,
    myIssuesResolved: 0,
  });
  const [devopsStats, setDevopsStats] = useState({
    assignedProjects: 0,
    assignedTasksTodo: 0,
    assignedTasksInProgress: 0,
    assignedIssuesOpen: 0,
    assignedIssuesInProgress: 0,
    assignedIssuesReopened: 0,
    assignedIssuesTotal: 0,
    assignedIssuesResolved: 0,
    myIssuesOpen: 0,
    myIssuesInProgress: 0,
    myIssuesReopened: 0,
    myIssuesTotal: 0,
    myIssuesResolved: 0,
  });
  const [hrStats, setHrStats] = useState({
    assignedTasksTodo: 0,
    assignedTasksInProgress: 0,
    assignedIssuesOpen: 0,
    assignedIssuesInProgress: 0,
    assignedIssuesReopened: 0,
    assignedIssuesTotal: 0,
  });

  const [developerIssuesCardExpanded, setDeveloperIssuesCardExpanded] = useState(false);
  const [testerIssuesCardExpanded, setTesterIssuesCardExpanded] = useState(false);
  const [hrIssuesCardExpanded, setHrIssuesCardExpanded] = useState(false);
  const [financeIssuesCardExpanded, setFinanceIssuesCardExpanded] = useState(false);
  const [salesIssuesCardExpanded, setSalesIssuesCardExpanded] = useState(false);
  const [devopsAssignedIssuesCardExpanded, setDevopsAssignedIssuesCardExpanded] = useState(false);
  const [devopsMyIssuesCardExpanded, setDevopsMyIssuesCardExpanded] = useState(false);

  const [baStats, setBaStats] = useState({
    activeProjects: 0,
    createdUserStories: 0,
  });
  const [financeStats, setFinanceStats] = useState({
    assignedTasksTodo: 0,
    assignedTasksInProgress: 0,
    assignedIssuesOpen: 0,
    assignedIssuesInProgress: 0,
    assignedIssuesReopened: 0,
    assignedIssuesTotal: 0,
    paymentsCompleted: 0,
    paymentsDue: 0,
  });
  const [salesStats, setSalesStats] = useState({
    assignedTasksTodo: 0,
    assignedTasksInProgress: 0,
    assignedIssuesOpen: 0,
    assignedIssuesInProgress: 0,
    assignedIssuesReopened: 0,
    assignedIssuesTotal: 0,
    digixModules: emptyDigixModules(),
  });
  const [permissionDigixModules, setPermissionDigixModules] = useState(() => emptyDigixModules());
  const salesDigixTotalLeads = useMemo(
    () => (salesStats.digixModules || []).reduce((sum, m) => sum + Number(m?.total ?? 0), 0),
    [salesStats.digixModules]
  );
  const permissionDigixTotalLeads = useMemo(
    () => (permissionDigixModules || []).reduce((sum, m) => sum + Number(m?.total ?? 0), 0),
    [permissionDigixModules]
  );
  const quickLinks = [
    { key: "projects", label: "Project Management", path: "/projects" },
    { key: "digixLeads", label: "Digix Leads", path: "/business-leads" },
    { key: "customers", label: "Clients", path: "/clients" },
    { key: "issues", label: "Issue Tracker", path: "/issues" },
  ];

  useEffect(() => {
    if (
      isAdmin ||
      isDeveloper ||
      isTester ||
      isDevops ||
      showHrDashboard ||
      showBaDashboard ||
      showFinanceDashboard ||
      showSalesDashboard
    ) {
      return;
    }
    let mounted = true;
    const load = async () => {
      try {
        const [projectsRes, leadsRes, realestateRes, trainingRes, jobRes, internshipRes, customersRes, issuesList] =
          await Promise.all([
            axiosInstance.get("/projects"),
            axiosInstance.get("/leads").catch(() => ({ data: [] })),
            axiosInstance.get("/real-estate-leads").catch(() => ({ data: [] })),
            axiosInstance.get("/training-leads").catch(() => ({ data: [] })),
            axiosInstance.get("/job-leads").catch(() => ({ data: [] })),
            axiosInstance.get("/internship-leads").catch(() => ({ data: [] })),
            axiosInstance.get("/clients").catch(() => ({ data: [] })),
            fetchIssues().catch(() => []),
          ]);
        if (!mounted) return;
        const digixTotal =
          toCount(leadsRes?.data ?? leadsRes) +
          toCount(realestateRes?.data ?? realestateRes) +
          toCount(trainingRes?.data ?? trainingRes) +
          toCount(jobRes?.data ?? jobRes) +
          toCount(internshipRes?.data ?? internshipRes);
        const uniqueIssues = dedupeIssuesById(issuesList);
        setCounts({
          projects: toCount(projectsRes?.data),
          digixLeads: digixTotal,
          customers: toCount(customersRes?.data),
          accounts: 0,
          issues: uniqueIssues.length,
        });
      } catch (err) {
        if (mounted) {
          setCounts({
            projects: 0,
            digixLeads: 0,
            customers: 0,
            accounts: 0,
            issues: 0,
          });
        }
      }
    };
    load();
    return () => { mounted = false; };
  }, [isAdmin, isDeveloper, isTester, isDevops, showHrDashboard, showBaDashboard, showFinanceDashboard, showSalesDashboard]);

  useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    const loadAdminStats = async () => {
      try {
        const [
          projectsRes,
          tasks,
          issuesList,
          leadsRes,
          realestateRes,
          trainingRes,
          internshipRes,
          jobRes,
          customersRes,
        ] = await Promise.all([
          axiosInstance.get("/projects").catch(() => ({ data: [] })),
          fetchAllTasks().catch(() => []),
          fetchIssues().catch(() => []),
          axiosInstance.get("/leads").catch(() => ({ data: [] })),
          axiosInstance.get("/real-estate-leads").catch(() => ({ data: [] })),
          axiosInstance.get("/training-leads").catch(() => ({ data: [] })),
          axiosInstance.get("/internship-leads").catch(() => ({ data: [] })),
          axiosInstance.get("/job-leads").catch(() => ({ data: [] })),
          axiosInstance.get("/clients").catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;

        const projects = Array.isArray(projectsRes?.data)
          ? projectsRes.data
          : Array.isArray(projectsRes?.data?.data)
            ? projectsRes.data.data
            : [];

        const taskCounts = { todo: 0, inProgress: 0 };
        for (const t of tasks || []) {
          const b = taskBucket(t?.Status ?? t?.status ?? t?.Status_Name ?? t?.status_name);
          if (b === "todo") taskCounts.todo += 1;
          if (b === "inProgress") taskCounts.inProgress += 1;
        }
        const subtasks = await fetchAllSubTasksFromTasks(tasks);
        const subtaskCounts = { todo: 0, inProgress: 0 };
        for (const st of subtasks || []) {
          const b = taskBucket(st?.Status ?? st?.status ?? st?.Status_Name ?? st?.status_name);
          if (b === "todo") subtaskCounts.todo += 1;
          if (b === "inProgress") subtaskCounts.inProgress += 1;
        }

        const digixModules = buildDigixModulesFromLeadResponses(
          leadsRes,
          realestateRes,
          trainingRes,
          jobRes,
          internshipRes
        );

        const customers = Array.isArray(customersRes?.data)
          ? customersRes.data
          : Array.isArray(customersRes?.data?.data)
            ? customersRes.data.data
            : [];
        const uniqueIssues = dedupeIssuesById(issuesList);
        setAdminStats({
          projectsNew: projects.filter(isNewProject).length,
          projectsInProgress: projects.filter(isActiveProject).length,
          projectsActive: projects.filter(isActiveProject).length,
          projectsCompleted: projects.filter(isResolvedProject).length,
          tasksTodo: taskCounts.todo,
          tasksInProgress: taskCounts.inProgress,
          subtasksTodo: subtaskCounts.todo,
          subtasksInProgress: subtaskCounts.inProgress,
          issuesOpen: uniqueIssues.filter(isOpenIssue).length,
          issuesInProgress: uniqueIssues.filter(isInProgressIssue).length,
          issuesReopened: uniqueIssues.filter(isReopenedIssue).length,
          digixModules,
          customersTotal: customers.length,
        });
      } catch {
        if (!mounted) return;
        setAdminStats({
          projectsNew: 0,
          projectsInProgress: 0,
          projectsActive: 0,
          projectsCompleted: 0,
          tasksTodo: 0,
          tasksInProgress: 0,
          subtasksTodo: 0,
          subtasksInProgress: 0,
          issuesOpen: 0,
          issuesInProgress: 0,
          issuesReopened: 0,
          digixModules: emptyDigixModules(),
          customersTotal: 0,
        });
      }
    };
    loadAdminStats();
    return () => {
      mounted = false;
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!isDeveloper || !currentUserId) return;
    let mounted = true;
    const loadDeveloperStats = async () => {
      try {
        const [projectsRes, tasks, allIssuesRaw] = await Promise.all([
          axiosInstance.get("/projects").catch(() => ({ data: [] })),
          fetchAllTasks().catch(() => []),
          fetchIssues().catch(() => []),
        ]);
        if (!mounted) return;

        const projects = Array.isArray(projectsRes?.data)
          ? projectsRes.data
          : Array.isArray(projectsRes?.data?.data)
            ? projectsRes.data.data
            : [];

        const assignedTaskRows = (tasks || []).filter((t) => isTaskAssignedToUser(t, currentUserId));
        const assignedSubtasks = await fetchAllSubTasksFromTasks(assignedTaskRows);
        const assignedTasksOverviewRows = buildTaskOverviewRows(assignedTaskRows, assignedSubtasks);

        let assignedTasksTodo = 0;
        let assignedTasksInProgress = 0;
        for (const t of assignedTaskRows) {
          const bucket = taskBucket(t?.Status ?? t?.status ?? t?.Status_Name ?? t?.status_name);
          if (bucket === "todo") assignedTasksTodo += 1;
          if (bucket === "inProgress") assignedTasksInProgress += 1;
        }

        const projectAssignmentChecks = await Promise.all(
          projects.map(async (p) => {
            if (isProjectAssignedToUserFromRow(p, currentUserId)) return true;
            const pid = p?.Project_Id ?? p?.project_id ?? p?.id;
            if (pid == null) return false;
            const users = await fetchProjectUsers(pid).catch(() => []);
            return (users || []).some((u) => {
              const uid = Number(u?.User_Id ?? u?.user_id ?? u?.id);
              return Number.isFinite(uid) && uid === Number(currentUserId);
            });
          })
        );
        const assignedProjectsFromChecks = projectAssignmentChecks.filter(Boolean).length;
        const assignedProjects = Math.max(assignedProjectsFromChecks, projects.length);

        const assignedIssues = dedupeIssuesById(allIssuesRaw).filter((i) => isIssueAssignedToUser(i, currentUserId));
        setDeveloperStats({
          assignedProjects,
          assignedTasksTodo,
          assignedTasksInProgress,
          assignedIssuesOpen: assignedIssues.filter(isOpenIssue).length,
          assignedIssuesInProgress: assignedIssues.filter(isInProgressIssue).length,
          assignedIssuesReopened: assignedIssues.filter(isReopenedIssue).length,
          assignedIssuesTotal: assignedIssues.length,
          assignedIssuesResolved: assignedIssues.filter(isResolvedIssue).length,
        });
        setRoleTasksOverviewRows(assignedTasksOverviewRows);
      } catch {
        if (!mounted) return;
        setDeveloperStats({
          assignedProjects: 0,
          assignedTasksTodo: 0,
          assignedTasksInProgress: 0,
          assignedIssuesOpen: 0,
          assignedIssuesInProgress: 0,
          assignedIssuesReopened: 0,
          assignedIssuesTotal: 0,
          assignedIssuesResolved: 0,
        });
        setRoleTasksOverviewRows([]);
      }
    };
    loadDeveloperStats();
    return () => {
      mounted = false;
    };
  }, [isDeveloper, currentUserId]);

  useEffect(() => {
    if (!isTester || !currentUserId) return;
    let mounted = true;
    const loadTesterStats = async () => {
      try {
        const [projectsRes, tasks, allIssues] = await Promise.all([
          axiosInstance.get("/projects").catch(() => ({ data: [] })),
          fetchAllTasks().catch(() => []),
          fetchIssues().catch(() => []),
        ]);
        if (!mounted) return;

        const projects = Array.isArray(projectsRes?.data)
          ? projectsRes.data
          : Array.isArray(projectsRes?.data?.data)
            ? projectsRes.data.data
            : [];

        const assignedTaskRows = (tasks || []).filter((t) => isTaskAssignedToUser(t, currentUserId));
        const assignedSubtasks = await fetchAllSubTasksFromTasks(assignedTaskRows);
        const assignedTasksOverviewRows = buildTaskOverviewRows(assignedTaskRows, assignedSubtasks);

        let assignedTasksTodo = 0;
        let assignedTasksInProgress = 0;
        for (const t of assignedTaskRows) {
          const bucket = taskBucket(t?.Status ?? t?.status ?? t?.Status_Name ?? t?.status_name);
          if (bucket === "todo") assignedTasksTodo += 1;
          if (bucket === "inProgress") assignedTasksInProgress += 1;
        }

        const projectAssignmentChecks = await Promise.all(
          projects.map(async (p) => {
            if (isProjectAssignedToUserFromRow(p, currentUserId)) return true;
            const pid = p?.Project_Id ?? p?.project_id ?? p?.id;
            if (pid == null) return false;
            const users = await fetchProjectUsers(pid).catch(() => []);
            return (users || []).some((u) => {
              const uid = Number(u?.User_Id ?? u?.user_id ?? u?.id);
              return Number.isFinite(uid) && uid === Number(currentUserId);
            });
          })
        );
        const assignedProjectsFromChecks = projectAssignmentChecks.filter(Boolean).length;
        const assignedProjects = Math.max(assignedProjectsFromChecks, projects.length);

        const myIssues = dedupeIssuesById(allIssues).filter((issue) => {
          const createdById = Number(issue?.Created_By ?? issue?.created_by);
          if (Number.isFinite(createdById) && createdById === Number(currentUserId)) return true;
          const createdByName = normalizeText(issue?.Created_By_Name ?? issue?.created_by_name);
          return currentUserName ? createdByName === normalizeText(currentUserName) : false;
        });

        setTesterStats({
          assignedProjects,
          assignedTasksTodo,
          assignedTasksInProgress,
          myIssuesOpen: myIssues.filter(isOpenIssue).length,
          myIssuesInProgress: myIssues.filter(isInProgressIssue).length,
          myIssuesReopened: myIssues.filter(isReopenedIssue).length,
          myIssuesTotal: myIssues.length,
          myIssuesResolved: myIssues.filter(isResolvedIssue).length,
        });
        setRoleTasksOverviewRows(assignedTasksOverviewRows);
      } catch {
        if (!mounted) return;
        setTesterStats({
          assignedProjects: 0,
          assignedTasksTodo: 0,
          assignedTasksInProgress: 0,
          myIssuesOpen: 0,
          myIssuesInProgress: 0,
          myIssuesReopened: 0,
          myIssuesTotal: 0,
          myIssuesResolved: 0,
        });
        setRoleTasksOverviewRows([]);
      }
    };
    loadTesterStats();
    return () => {
      mounted = false;
    };
  }, [isTester, currentUserId, currentUserName]);

  useEffect(() => {
    if (!isDevops || !currentUserId) return;
    let mounted = true;
    const loadDevopsStats = async () => {
      try {
        const [projectsRes, tasks, assignedIssuesByParamRaw, allIssuesRaw] = await Promise.all([
          axiosInstance.get("/projects").catch(() => ({ data: [] })),
          fetchAllTasks().catch(() => []),
          fetchIssues({ assignedUserId: currentUserId }).catch(() => []),
          fetchIssues().catch(() => []),
        ]);
        if (!mounted) return;

        const projects = Array.isArray(projectsRes?.data)
          ? projectsRes.data
          : Array.isArray(projectsRes?.data?.data)
            ? projectsRes.data.data
            : [];

        const assignedTaskRows = (tasks || []).filter((t) => isTaskAssignedToUser(t, currentUserId));
        const assignedSubtasks = await fetchAllSubTasksFromTasks(assignedTaskRows);
        const assignedTasksOverviewRows = buildTaskOverviewRows(assignedTaskRows, assignedSubtasks);

        let assignedTasksTodo = 0;
        let assignedTasksInProgress = 0;
        for (const t of assignedTaskRows) {
          const bucket = taskBucket(t?.Status ?? t?.status ?? t?.Status_Name ?? t?.status_name);
          if (bucket === "todo") assignedTasksTodo += 1;
          if (bucket === "inProgress") assignedTasksInProgress += 1;
        }

        const projectAssignmentChecks = await Promise.all(
          projects.map(async (p) => {
            if (isProjectAssignedToUserFromRow(p, currentUserId)) return true;
            const pid = p?.Project_Id ?? p?.project_id ?? p?.id;
            if (pid == null) return false;
            const users = await fetchProjectUsers(pid).catch(() => []);
            return (users || []).some((u) => {
              const uid = Number(u?.User_Id ?? u?.user_id ?? u?.id);
              return Number.isFinite(uid) && uid === Number(currentUserId);
            });
          })
        );
        const assignedProjectsFromChecks = projectAssignmentChecks.filter(Boolean).length;
        const assignedProjects = Math.max(assignedProjectsFromChecks, projects.length);

        const dedupedIssues = dedupeIssuesById(allIssuesRaw);
        const assignedIssuesFromRows = dedupedIssues.filter((i) => isIssueAssignedToUser(i, currentUserId));
        const assignedIssuesByParam = dedupeIssuesById(assignedIssuesByParamRaw);
        const assignedIssues =
          assignedIssuesFromRows.length >= assignedIssuesByParam.length
            ? assignedIssuesFromRows
            : assignedIssuesByParam;
        const myIssues = dedupedIssues.filter((issue) => {
          const createdById = Number(issue?.Created_By ?? issue?.created_by);
          if (Number.isFinite(createdById) && createdById === Number(currentUserId)) return true;
          const createdByName = normalizeText(issue?.Created_By_Name ?? issue?.created_by_name);
          return currentUserName ? createdByName === normalizeText(currentUserName) : false;
        });

        setDevopsStats({
          assignedProjects,
          assignedTasksTodo,
          assignedTasksInProgress,
          assignedIssuesOpen: assignedIssues.filter(isOpenIssue).length,
          assignedIssuesInProgress: assignedIssues.filter(isInProgressIssue).length,
          assignedIssuesReopened: assignedIssues.filter(isReopenedIssue).length,
          assignedIssuesTotal: assignedIssues.length,
          assignedIssuesResolved: assignedIssues.filter(isResolvedIssue).length,
          myIssuesOpen: myIssues.filter(isOpenIssue).length,
          myIssuesInProgress: myIssues.filter(isInProgressIssue).length,
          myIssuesReopened: myIssues.filter(isReopenedIssue).length,
          myIssuesTotal: myIssues.length,
          myIssuesResolved: myIssues.filter(isResolvedIssue).length,
        });
        setRoleTasksOverviewRows(assignedTasksOverviewRows);
      } catch {
        if (!mounted) return;
        setDevopsStats({
          assignedProjects: 0,
          assignedTasksTodo: 0,
          assignedTasksInProgress: 0,
          assignedIssuesOpen: 0,
          assignedIssuesInProgress: 0,
          assignedIssuesReopened: 0,
          assignedIssuesTotal: 0,
          assignedIssuesResolved: 0,
          myIssuesOpen: 0,
          myIssuesInProgress: 0,
          myIssuesReopened: 0,
          myIssuesTotal: 0,
          myIssuesResolved: 0,
        });
        setRoleTasksOverviewRows([]);
      }
    };
    loadDevopsStats();
    return () => {
      mounted = false;
    };
  }, [isDevops, currentUserId, currentUserName]);

  useEffect(() => {
    if (!showHrDashboard) return;
    if (!currentUserId) {
      setHrStats({
        assignedTasksTodo: 0,
        assignedTasksInProgress: 0,
        assignedIssuesOpen: 0,
        assignedIssuesInProgress: 0,
        assignedIssuesReopened: 0,
        assignedIssuesTotal: 0,
      });
      return;
    }
    let mounted = true;
    const loadHrStats = async () => {
      try {
        const [tasks, allIssuesRaw] = await Promise.all([
          fetchAllTasks().catch(() => []),
          fetchIssues().catch(() => []),
        ]);
        if (!mounted) return;

        const assignedTaskRows = (tasks || []).filter((t) => isTaskAssignedToUser(t, currentUserId));
        const assignedSubtasks = await fetchAllSubTasksFromTasks(assignedTaskRows);
        const assignedTasksOverviewRows = buildTaskOverviewRows(assignedTaskRows, assignedSubtasks);
        let assignedTasksTodo = 0;
        let assignedTasksInProgress = 0;
        for (const t of assignedTaskRows) {
          const bucket = taskBucket(t?.Status ?? t?.status ?? t?.Status_Name ?? t?.status_name);
          if (bucket === "todo") assignedTasksTodo += 1;
          if (bucket === "inProgress") assignedTasksInProgress += 1;
        }

        const assignedIssues = dedupeIssuesById(allIssuesRaw).filter((i) =>
          isIssueAssignedToUser(i, currentUserId)
        );
        setHrStats({
          assignedTasksTodo,
          assignedTasksInProgress,
          assignedIssuesOpen: assignedIssues.filter(isOpenIssue).length,
          assignedIssuesInProgress: assignedIssues.filter(isInProgressIssue).length,
          assignedIssuesReopened: assignedIssues.filter(isReopenedIssue).length,
          assignedIssuesTotal: assignedIssues.length,
        });
        setRoleTasksOverviewRows(assignedTasksOverviewRows);
      } catch {
        if (!mounted) return;
        setHrStats({
          assignedTasksTodo: 0,
          assignedTasksInProgress: 0,
          assignedIssuesOpen: 0,
          assignedIssuesInProgress: 0,
          assignedIssuesReopened: 0,
          assignedIssuesTotal: 0,
        });
        setRoleTasksOverviewRows([]);
      }
    };
    loadHrStats();
    return () => {
      mounted = false;
    };
  }, [showHrDashboard, currentUserId]);

  useEffect(() => {
    if (!showBaDashboard) return;
    let mounted = true;
    const loadBaStats = async () => {
      try {
        const projectsRes = await axiosInstance.get("/projects").catch(() => ({ data: [] }));
        const projects = Array.isArray(projectsRes?.data)
          ? projectsRes.data
          : Array.isArray(projectsRes?.data?.data)
            ? projectsRes.data.data
            : [];
        if (!mounted) return;
        const activeProjects = projects.filter(isActiveProject).length;
        let createdCount = 0;
        if (currentUserId != null || currentUserName) {
          const nameNorm = normalizeText(currentUserName);
          const allStories = await fetchAllUserStoriesAggregated(projects);
          if (!mounted) return;
          createdCount = allStories.filter((s) =>
            isUserStoryCreatedByUser(s, currentUserId, nameNorm)
          ).length;
        }
        setBaStats({ activeProjects, createdUserStories: createdCount });
      } catch {
        if (!mounted) return;
        setBaStats({ activeProjects: 0, createdUserStories: 0 });
      }
    };
    loadBaStats();
    return () => {
      mounted = false;
    };
  }, [showBaDashboard, currentUserId, currentUserName]);

  useEffect(() => {
    if (!showFinanceDashboard) return;
    let mounted = true;
    const loadFinanceStats = async () => {
      try {
        const [tasks, allIssuesRaw, paymentRows] = await Promise.all([
          fetchAllTasks().catch(() => []),
          fetchIssues().catch(() => []),
          fetchAllProjectPaymentsAggregated().catch(() => []),
        ]);
        if (!mounted) return;

        let assignedTasksTodo = 0;
        let assignedTasksInProgress = 0;
        let assignedIssuesOpen = 0;
        let assignedIssuesInProgress = 0;
        let assignedIssuesReopened = 0;
        let assignedIssuesTotal = 0;
        if (currentUserId != null) {
          const assignedTaskRows = (tasks || []).filter((t) => isTaskAssignedToUser(t, currentUserId));
          const assignedSubtasks = await fetchAllSubTasksFromTasks(assignedTaskRows);
          const assignedTasksOverviewRows = buildTaskOverviewRows(assignedTaskRows, assignedSubtasks);
          for (const t of assignedTaskRows) {
            const bucket = taskBucket(t?.Status ?? t?.status ?? t?.Status_Name ?? t?.status_name);
            if (bucket === "todo") assignedTasksTodo += 1;
            if (bucket === "inProgress") assignedTasksInProgress += 1;
          }
          const assignedIssues = dedupeIssuesById(allIssuesRaw).filter((i) =>
            isIssueAssignedToUser(i, currentUserId)
          );
          assignedIssuesOpen = assignedIssues.filter(isOpenIssue).length;
          assignedIssuesInProgress = assignedIssues.filter(isInProgressIssue).length;
          assignedIssuesReopened = assignedIssues.filter(isReopenedIssue).length;
          assignedIssuesTotal = assignedIssues.length;
          setRoleTasksOverviewRows(assignedTasksOverviewRows);
        }

        const { completed, due } = financePaymentCompletedDueCounts(paymentRows);
        setFinanceStats({
          assignedTasksTodo,
          assignedTasksInProgress,
          assignedIssuesOpen,
          assignedIssuesInProgress,
          assignedIssuesReopened,
          assignedIssuesTotal,
          paymentsCompleted: completed,
          paymentsDue: due,
        });
      } catch {
        if (!mounted) return;
        setFinanceStats({
          assignedTasksTodo: 0,
          assignedTasksInProgress: 0,
          assignedIssuesOpen: 0,
          assignedIssuesInProgress: 0,
          assignedIssuesReopened: 0,
          assignedIssuesTotal: 0,
          paymentsCompleted: 0,
          paymentsDue: 0,
        });
        setRoleTasksOverviewRows([]);
      }
    };
    loadFinanceStats();
    return () => {
      mounted = false;
    };
  }, [showFinanceDashboard, currentUserId]);

  useEffect(() => {
    if (!showSalesDashboard) return;
    let mounted = true;
    const loadSalesStats = async () => {
      try {
        const [tasks, allIssuesRaw, leadsRes, realestateRes, trainingRes, internshipRes, jobRes] =
          await Promise.all([
            fetchAllTasks().catch(() => []),
            fetchIssues().catch(() => []),
            axiosInstance.get("/leads").catch(() => ({ data: [] })),
            axiosInstance.get("/real-estate-leads").catch(() => ({ data: [] })),
            axiosInstance.get("/training-leads").catch(() => ({ data: [] })),
            axiosInstance.get("/internship-leads").catch(() => ({ data: [] })),
            axiosInstance.get("/job-leads").catch(() => ({ data: [] })),
          ]);
        if (!mounted) return;

        let assignedTasksTodo = 0;
        let assignedTasksInProgress = 0;
        let assignedIssuesOpen = 0;
        let assignedIssuesInProgress = 0;
        let assignedIssuesReopened = 0;
        let assignedIssuesTotal = 0;
        if (currentUserId != null) {
          const assignedTaskRows = (tasks || []).filter((t) => isTaskAssignedToUser(t, currentUserId));
          const assignedSubtasks = await fetchAllSubTasksFromTasks(assignedTaskRows);
          const assignedTasksOverviewRows = buildTaskOverviewRows(assignedTaskRows, assignedSubtasks);
          for (const t of assignedTaskRows) {
            const bucket = taskBucket(t?.Status ?? t?.status ?? t?.Status_Name ?? t?.status_name);
            if (bucket === "todo") assignedTasksTodo += 1;
            if (bucket === "inProgress") assignedTasksInProgress += 1;
          }
          const assignedIssues = dedupeIssuesById(allIssuesRaw).filter((i) =>
            isIssueAssignedToUser(i, currentUserId)
          );
          assignedIssuesOpen = assignedIssues.filter(isOpenIssue).length;
          assignedIssuesInProgress = assignedIssues.filter(isInProgressIssue).length;
          assignedIssuesReopened = assignedIssues.filter(isReopenedIssue).length;
          assignedIssuesTotal = assignedIssues.length;
          setRoleTasksOverviewRows(assignedTasksOverviewRows);
        }

        const digixModules = buildDigixModulesFromLeadResponses(
          leadsRes,
          realestateRes,
          trainingRes,
          jobRes,
          internshipRes
        );

        setSalesStats({
          assignedTasksTodo,
          assignedTasksInProgress,
          assignedIssuesOpen,
          assignedIssuesInProgress,
          assignedIssuesReopened,
          assignedIssuesTotal,
          digixModules,
        });
      } catch {
        if (!mounted) return;
        setSalesStats({
          assignedTasksTodo: 0,
          assignedTasksInProgress: 0,
          assignedIssuesOpen: 0,
          assignedIssuesInProgress: 0,
          assignedIssuesReopened: 0,
          assignedIssuesTotal: 0,
          digixModules: emptyDigixModules(),
        });
        setRoleTasksOverviewRows([]);
      }
    };
    loadSalesStats();
    return () => {
      mounted = false;
    };
  }, [showSalesDashboard, currentUserId]);

  useEffect(() => {
    if (isDeveloper || isTester || isDevops || showHrDashboard || showFinanceDashboard || showSalesDashboard) {
      return;
    }
    setRoleTasksCardExpanded(false);
    setRoleTasksOverviewRows([]);
    setDeveloperIssuesCardExpanded(false);
    setTesterIssuesCardExpanded(false);
    setHrIssuesCardExpanded(false);
    setFinanceIssuesCardExpanded(false);
    setSalesIssuesCardExpanded(false);
    setDevopsAssignedIssuesCardExpanded(false);
    setDevopsMyIssuesCardExpanded(false);
  }, [isDeveloper, isTester, isDevops, showHrDashboard, showFinanceDashboard, showSalesDashboard]);

  useEffect(() => {
    const allowed = getAllowedSidebarPages(role);
    const wantPermissionDigixTable =
      !isAdmin &&
      !showSalesDashboard &&
      !isDeveloper &&
      !isTester &&
      !isDevops &&
      !showHrDashboard &&
      !showBaDashboard &&
      !showFinanceDashboard &&
      allowed.includes("DigixLeads");
    if (!wantPermissionDigixTable) return;
    let mounted = true;
    const load = async () => {
      try {
        const modules = await fetchDigixModulesFromApis();
        if (mounted) setPermissionDigixModules(modules);
      } catch {
        if (mounted) setPermissionDigixModules(emptyDigixModules());
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [
    role,
    isAdmin,
    showSalesDashboard,
    isDeveloper,
    isTester,
    isDevops,
    showHrDashboard,
    showBaDashboard,
    showFinanceDashboard,
  ]);

  return (
    <div className="projects-container">
      <div className="main-card">
        {isAdmin ? (
          <div className="dashboard-admin-layout">
            <div className="dashboard-admin-summary-row">
              <div className="dashboard-summary-tile">
                <h3 className="dashboard-summary-tile__title">Projects</h3>
                <div className="dashboard-summary-tile__body">
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate("/projects", {
                        state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "new" },
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/projects", {
                          state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "new" },
                        });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">New</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.projectsNew}</span>
                  </div>
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate("/projects", {
                        state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "active" },
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/projects", {
                          state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "active" },
                        });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">Active</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.projectsInProgress}</span>
                  </div>
                </div>
              </div>
              <div className="dashboard-summary-tile">
                <h3 className="dashboard-summary-tile__title">Tasks</h3>
                <div className="dashboard-summary-tile__body">
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate("/projects", {
                        state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "todo" },
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/projects", {
                          state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "todo" },
                        });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">To do</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">
                      {Number(adminStats.tasksTodo || 0) + Number(adminStats.subtasksTodo || 0)}
                    </span>
                  </div>
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate("/projects", {
                        state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "inProgress" },
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/projects", {
                          state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "inProgress" },
                        });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">In progress</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">
                      {Number(adminStats.tasksInProgress || 0) + Number(adminStats.subtasksInProgress || 0)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="dashboard-summary-tile">
                <h3 className="dashboard-summary-tile__title">Issues</h3>
                <div className="dashboard-summary-tile__body">
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate("/issues", { state: { dashboardIssueStatusPreset: "open" } })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/issues", { state: { dashboardIssueStatusPreset: "open" } });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">Open</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.issuesOpen}</span>
                  </div>
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate("/issues", { state: { dashboardIssueStatusPreset: "inProgress" } })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/issues", { state: { dashboardIssueStatusPreset: "inProgress" } });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">In progress</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.issuesInProgress}</span>
                  </div>
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate("/issues", { state: { dashboardIssueStatusPreset: "reopened" } })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/issues", { state: { dashboardIssueStatusPreset: "reopened" } });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">Reopened</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.issuesReopened}</span>
                  </div>
                </div>
              </div>
              <div className="dashboard-summary-tile">
                <h3 className="dashboard-summary-tile__title">Customers</h3>
                <div className="dashboard-summary-tile__body">
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate("/clients")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/clients");
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">Total customers</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.customersTotal}</span>
                  </div>
                  <div
                    className="dashboard-summary-tile__line dashboard-summary-tile__line-btn"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate("/clients", {
                        state: { dashboardCustomerStatusPreset: "activeProjects" },
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/clients", {
                          state: { dashboardCustomerStatusPreset: "activeProjects" },
                        });
                      }
                    }}
                  >
                    <span className="dashboard-summary-tile__label">Active projects</span>
                    <span className="dashboard-summary-tile__sep"> : </span>
                    <span className="dashboard-summary-tile__num">{adminStats.projectsActive}</span>
                  </div>
                </div>
              </div>
            </div>

            <DashboardDigixTable modules={adminStats.digixModules} navigate={navigate} />
          </div>
        ) : isDeveloper ? (
          <div className="dashboard-admin-stack">
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Projects</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() =>
                    navigate("/projects", {
                      state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "assigned" }
                    })
                  }
                >
                  <span className="dashboard-module-metric__label">Assigned Projects</span>
                  <span className="dashboard-module-metric__value">{developerStats.assignedProjects}</span>
                </button>
              </div>
            </div>
            <div
              className="dashboard-module-card"
              role="button"
              tabIndex={0}
              onClick={() => setRoleTasksCardExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRoleTasksCardExpanded((v) => !v);
                }
              }}
            >
              <h3 className="dashboard-module-card__title">Assigned Tasks</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/projects", { state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "todo" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">To-do</span>
                  <span className="dashboard-module-metric__value">{developerStats.assignedTasksTodo}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/projects", { state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "inProgress" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">In Progress</span>
                  <span className="dashboard-module-metric__value">{developerStats.assignedTasksInProgress}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Assigned Issues</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setDeveloperIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{developerStats.assignedIssuesTotal}</span>
                </button>
              </div>
            </div>
            {developerIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: developerStats.assignedIssuesOpen,
                    inProgress: developerStats.assignedIssuesInProgress,
                    reopened: developerStats.assignedIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            {roleTasksCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Tasks Overview"
              >
                <DashboardTasksOverviewTable rows={roleTasksOverviewRows} navigate={navigate} />
              </DashboardCollapsibleTableCard>
            ) : null}
          </div>
        ) : isTester ? (
          <div className="dashboard-admin-stack">
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Projects</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() =>
                    navigate("/projects", {
                      state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "assigned" }
                    })
                  }
                >
                  <span className="dashboard-module-metric__label">Assigned Projects</span>
                  <span className="dashboard-module-metric__value">{testerStats.assignedProjects}</span>
                </button>
              </div>
            </div>
            <div
              className="dashboard-module-card"
              role="button"
              tabIndex={0}
              onClick={() => setRoleTasksCardExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRoleTasksCardExpanded((v) => !v);
                }
              }}
            >
              <h3 className="dashboard-module-card__title">Assigned Tasks</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/projects", { state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "todo" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">To-do</span>
                  <span className="dashboard-module-metric__value">{testerStats.assignedTasksTodo}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/projects", { state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "inProgress" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">In Progress</span>
                  <span className="dashboard-module-metric__value">{testerStats.assignedTasksInProgress}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Issues Assigned by Me</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setTesterIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{testerStats.myIssuesTotal}</span>
                </button>
              </div>
            </div>
            {testerIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: testerStats.myIssuesOpen,
                    inProgress: testerStats.myIssuesInProgress,
                    reopened: testerStats.myIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            {roleTasksCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Tasks Overview"
              >
                <DashboardTasksOverviewTable rows={roleTasksOverviewRows} navigate={navigate} />
              </DashboardCollapsibleTableCard>
            ) : null}
          </div>
        ) : showHrDashboard ? (
          <div className="dashboard-admin-stack">
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">HR Team</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/hr-management", { state: { hrTab: "recruitment" } })}
                >
                  <span className="dashboard-module-metric__label">Recruitment</span>
                  <span className="dashboard-module-metric__value">—</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/hr-management", { state: { hrTab: "time" } })}
                >
                  <span className="dashboard-module-metric__label">Time management</span>
                  <span className="dashboard-module-metric__value">—</span>
                </button>
              </div>
            </div>
            <div
              className="dashboard-module-card"
              role="button"
              tabIndex={0}
              onClick={() => setRoleTasksCardExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRoleTasksCardExpanded((v) => !v);
                }
              }}
            >
              <h3 className="dashboard-module-card__title">Tasks</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/projects", { state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "todo" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">To-do</span>
                  <span className="dashboard-module-metric__value">{hrStats.assignedTasksTodo}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/projects", { state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "inProgress" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">In Progress</span>
                  <span className="dashboard-module-metric__value">{hrStats.assignedTasksInProgress}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Issues</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setHrIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{hrStats.assignedIssuesTotal}</span>
                </button>
              </div>
            </div>
            {hrIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: hrStats.assignedIssuesOpen,
                    inProgress: hrStats.assignedIssuesInProgress,
                    reopened: hrStats.assignedIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            {roleTasksCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Tasks Overview"
              >
                <DashboardTasksOverviewTable rows={roleTasksOverviewRows} navigate={navigate} />
              </DashboardCollapsibleTableCard>
            ) : null}
          </div>
        ) : showBaDashboard ? (
          <div className="dashboard-admin-stack">
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Projects</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/projects", { state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "active" } })}
                >
                  <span className="dashboard-module-metric__label">Active projects</span>
                  <span className="dashboard-module-metric__value">{baStats.activeProjects}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">User Stories</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/projects", { state: { projectManagementTab: "User Stories" } })}
                >
                  <span className="dashboard-module-metric__label">Created user stories</span>
                  <span className="dashboard-module-metric__value">{baStats.createdUserStories}</span>
                </button>
              </div>
            </div>
          </div>
        ) : showFinanceDashboard ? (
          <div className="dashboard-admin-stack">
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Finance Management</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/finance-management", { state: { financeTab: "payable" } })}
                >
                  <span className="dashboard-module-metric__label">Accounts receivable</span>
                  <span className="dashboard-module-metric__value">-</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/finance-management", { state: { financeTab: "receivable" } })}
                >
                  <span className="dashboard-module-metric__label">Accounts payable</span>
                  <span className="dashboard-module-metric__value">-</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/finance-management", { state: { financeTab: "accounts" } })}
                >
                  <span className="dashboard-module-metric__label">Accounts</span>
                  <span className="dashboard-module-metric__value">-</span>
                </button>
              </div>
            </div>
            <div
              className="dashboard-module-card"
              role="button"
              tabIndex={0}
              onClick={() => setRoleTasksCardExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRoleTasksCardExpanded((v) => !v);
                }
              }}
            >
              <h3 className="dashboard-module-card__title">Tasks</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/projects", { state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "todo" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">To-do</span>
                  <span className="dashboard-module-metric__value">{financeStats.assignedTasksTodo}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/projects", { state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "inProgress" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">In Progress</span>
                  <span className="dashboard-module-metric__value">{financeStats.assignedTasksInProgress}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Issues</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setFinanceIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{financeStats.assignedIssuesTotal}</span>
                </button>
              </div>
            </div>
            {financeIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: financeStats.assignedIssuesOpen,
                    inProgress: financeStats.assignedIssuesInProgress,
                    reopened: financeStats.assignedIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Payments</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() =>
                    navigate("/projects", {
                      state: { projectManagementTab: "Projects", dashboardProjectStatusPreset: "assigned" }
                    })
                  }
                >
                  <span className="dashboard-module-metric__label">Completed</span>
                  <span className="dashboard-module-metric__value">{financeStats.paymentsCompleted}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/projects", { state: { projectManagementTab: "Projects" } })}
                >
                  <span className="dashboard-module-metric__label">Due</span>
                  <span className="dashboard-module-metric__value">{financeStats.paymentsDue}</span>
                </button>
              </div>
            </div>
            {roleTasksCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Tasks Overview"
              >
                <DashboardTasksOverviewTable rows={roleTasksOverviewRows} navigate={navigate} />
              </DashboardCollapsibleTableCard>
            ) : null}
          </div>
        ) : showSalesDashboard ? (
          <div className="dashboard-admin-stack">
            <div
              className="dashboard-module-card"
              role="button"
              tabIndex={0}
              onClick={() => setRoleTasksCardExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRoleTasksCardExpanded((v) => !v);
                }
              }}
            >
              <h3 className="dashboard-module-card__title">Tasks</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/projects", { state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "todo" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">To-do</span>
                  <span className="dashboard-module-metric__value">{salesStats.assignedTasksTodo}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/projects", { state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "inProgress" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">In Progress</span>
                  <span className="dashboard-module-metric__value">{salesStats.assignedTasksInProgress}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Issues</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setSalesIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{salesStats.assignedIssuesTotal}</span>
                </button>
              </div>
            </div>
            {salesIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: salesStats.assignedIssuesOpen,
                    inProgress: salesStats.assignedIssuesInProgress,
                    reopened: salesStats.assignedIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">DigixLeads</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setSalesDigixCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total leads</span>
                  <span className="dashboard-module-metric__value">{salesDigixTotalLeads}</span>
                </button>
              </div>
            </div>
            {salesDigixCardExpanded ? (
              <DashboardDigixTable modules={salesStats.digixModules} navigate={navigate} />
            ) : null}
            {roleTasksCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Tasks Overview"
              >
                <DashboardTasksOverviewTable rows={roleTasksOverviewRows} navigate={navigate} />
              </DashboardCollapsibleTableCard>
            ) : null}
          </div>
        ) : isDevops ? (
          <div className="dashboard-admin-stack">
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Projects</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => navigate("/projects", { state: { projectManagementTab: "Projects" } })}
                >
                  <span className="dashboard-module-metric__label">Assigned Projects</span>
                  <span className="dashboard-module-metric__value">{devopsStats.assignedProjects}</span>
                </button>
              </div>
            </div>
            <div
              className="dashboard-module-card"
              role="button"
              tabIndex={0}
              onClick={() => setRoleTasksCardExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRoleTasksCardExpanded((v) => !v);
                }
              }}
            >
              <h3 className="dashboard-module-card__title">Assigned Tasks</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/projects", { state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "todo" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">To-do</span>
                  <span className="dashboard-module-metric__value">{devopsStats.assignedTasksTodo}</span>
                </button>
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/projects", { state: { projectManagementTab: "Tasks", dashboardTaskStatusPreset: "inProgress" } });
                  }}
                >
                  <span className="dashboard-module-metric__label">In Progress</span>
                  <span className="dashboard-module-metric__value">{devopsStats.assignedTasksInProgress}</span>
                </button>
              </div>
            </div>
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Assigned Issues</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setDevopsAssignedIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{devopsStats.assignedIssuesTotal}</span>
                </button>
              </div>
            </div>
            {devopsAssignedIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: devopsStats.assignedIssuesOpen,
                    inProgress: devopsStats.assignedIssuesInProgress,
                    reopened: devopsStats.assignedIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            <div className="dashboard-module-card">
              <h3 className="dashboard-module-card__title">Issues Assigned by Me</h3>
              <div className="dashboard-module-card__metrics">
                <button
                  type="button"
                  className="dashboard-module-metric"
                  onClick={() => setDevopsMyIssuesCardExpanded((v) => !v)}
                >
                  <span className="dashboard-module-metric__label">Total issues</span>
                  <span className="dashboard-module-metric__value">{devopsStats.myIssuesTotal}</span>
                </button>
              </div>
            </div>
            {devopsMyIssuesCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Issues Overview"
              >
                <DashboardIssuesOverviewTable
                  counts={{
                    open: devopsStats.myIssuesOpen,
                    inProgress: devopsStats.myIssuesInProgress,
                    reopened: devopsStats.myIssuesReopened,
                  }}
                  navigate={navigate}
                />
              </DashboardCollapsibleTableCard>
            ) : null}
            {roleTasksCardExpanded ? (
              <DashboardCollapsibleTableCard
                cardClassName="dashboard-customer-projects-card"
                title="Assigned Tasks Overview"
              >
                <DashboardTasksOverviewTable rows={roleTasksOverviewRows} navigate={navigate} />
              </DashboardCollapsibleTableCard>
            ) : null}
          </div>
        ) : (
          <div className="dashboard-admin-stack">
            {quickLinks
              .filter((link) => allowedPages.includes(CARD_KEY_TO_PAGE[link.key]))
              .map((link) => {
                if (link.key === "digixLeads") {
                  return (
                    <div key={link.path} className="dashboard-module-card">
                      <h3 className="dashboard-module-card__title">DigixLeads</h3>
                      <div className="dashboard-module-card__metrics">
                        <button
                          type="button"
                          className="dashboard-module-metric"
                          onClick={() => setPermissionDigixCardExpanded((v) => !v)}
                        >
                          <span className="dashboard-module-metric__label">Total leads</span>
                          <span className="dashboard-module-metric__value">{permissionDigixTotalLeads}</span>
                        </button>
                      </div>
                      {permissionDigixCardExpanded ? (
                        <DashboardDigixTable
                          modules={permissionDigixModules}
                          navigate={navigate}
                        />
                      ) : null}
                    </div>
                  );
                }
                const count = counts[link.key];
                return (
                  <div key={link.path} className="dashboard-module-card">
                    <h3 className="dashboard-module-card__title">{link.label}</h3>
                    <div className="dashboard-module-card__metrics">
                      <button
                        type="button"
                        className="dashboard-module-metric"
                        onClick={() => navigate(link.path)}
                      >
                        <span className="dashboard-module-metric__label">Total</span>
                        <span className="dashboard-module-metric__value">
                          {count != null ? count : "—"}
                        </span>
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
